import { Component, type ErrorInfo, type ReactNode, type ReactElement } from 'react'

interface Props {
  children: ReactNode
}

interface State {
  error: Error | null
  info: ErrorInfo | null
}

/**
 * Catches any error thrown during a descendant's render or lifecycle and
 * shows a diagnostic screen instead of React's default blank-tree behavior.
 *
 * Important for this demo: blank screens on real iOS Safari were impossible
 * to diagnose remotely because there was no surfaced error — only a white
 * page. The boundary makes the failure mode visible (error.message, stack,
 * Reset button) so the operator can read it off the phone and report back.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null, info: null }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Mirror to the console so devtools (Safari Web Inspector when tethered)
    // also catch the trace.
    console.error('[ErrorBoundary]', error, info)
    this.setState({ info })
  }

  private reset = (): void => {
    try { sessionStorage.clear() } catch { /* */ }
    const params = new URLSearchParams(window.location.search)
    params.delete('step')
    window.location.href = `${window.location.pathname}?${params.toString()}`
  }

  override render(): ReactNode {
    if (!this.state.error) return this.props.children
    const e = this.state.error
    return (
      <div className="min-h-[100dvh] bg-background text-foreground p-6 flex flex-col gap-4 overflow-y-auto">
        <h1 className="text-xl font-semibold">Something went wrong</h1>
        <p className="text-sm text-muted-foreground">
          A screen failed to render. The exact error is below — please screenshot this if you're reporting it.
        </p>
        <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-4 text-sm">
          <div className="font-semibold text-destructive break-all">{e.name}: {e.message}</div>
          {e.stack && (
            <pre className="text-xs text-muted-foreground mt-3 overflow-x-auto whitespace-pre-wrap break-words">
              {e.stack}
            </pre>
          )}
        </div>
        <button
          type="button"
          onClick={this.reset}
          className="rounded-full bg-primary text-primary-foreground py-3 px-5 font-semibold mt-2"
        >
          Reset and start over
        </button>
      </div>
    )
  }
}

const Wrapper = ({ children }: Props): ReactElement => <ErrorBoundary>{children}</ErrorBoundary>
export default Wrapper
